import React, { useState } from 'react';
import {
  CheckCircle,
  AlertCircle,
  Download,
  Upload,
  ExternalLink,
  ArrowRight,
  ArrowLeft,
  RefreshCw,
  FileSpreadsheet,
  Zap,
  User,
  Mail,
  Tag,
  Calendar,
  Edit3,
  Eye,
  Copy,
  Check,
  HelpCircle,
} from 'lucide-react';
import { useData } from '../context/DataContext';
import { supabase } from '../utils/supabaseIntegration';

const GoogleSheetsSetupWizard = ({ setCurrentPage }) => {
  const { saveSheetConfig, user } = useData();

  // Debug environment variables at component load
  React.useEffect(() => {
    console.log('🔧 Environment Debug:', {
      supabaseUrl: process.env.REACT_APP_SUPABASE_URL ? 'Set' : 'Missing',
      supabaseKey: process.env.REACT_APP_SUPABASE_ANON_KEY ? 'Set' : 'Missing',
      nodeEnv: process.env.NODE_ENV,
      allReactAppVars: Object.keys(process.env || {}).filter((k) =>
        k.startsWith('REACT_APP')
      ),
    });
  }, []);

  const [currentStep, setCurrentStep] = useState(1);
  const [setupMethod, setSetupMethod] = useState(null); // 'import' or 'template'
  const [sheetUrl, setSheetUrl] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [columnMapping, setColumnMapping] = useState({});
  const [isConnected, setIsConnected] = useState(false);
  const [copiedTemplate, setCopiedTemplate] = useState(false);

  // Sample data that might be detected from their sheet
  const [
    detectedData,
    /* eslint-disable no-unused-vars */ setDetectedData /* eslint-disable no-unused-vars */,
  ] = useState([]);

  // Column types we're looking for
  const targetColumns = [
    {
      key: 'id',
      label: 'Client ID',
      description: 'Unique identifier (numbers, letters, or combination)',
      icon: Tag,
      required: false,
      examples: ['ID', 'Client ID', 'Reference', 'Number', '#'],
    },
    {
      key: 'name',
      label: 'Client Name',
      description: 'Full name of the client',
      icon: User,
      required: true,
      examples: ['Name', 'Client Name', 'Full Name', 'Customer'],
    },
    {
      key: 'email',
      label: 'Email Address',
      description: 'Client email for status lookup',
      icon: Mail,
      required: true,
      examples: ['Email', 'Email Address', 'Contact Email'],
    },
    {
      key: 'status',
      label: 'Current Status',
      description: 'Project or service status',
      icon: CheckCircle,
      required: true,
      examples: ['Status', 'Progress', 'Stage', 'Phase'],
    },
    {
      key: 'priority',
      label: 'Priority/Position',
      description: 'Queue position or priority level',
      icon: ArrowRight,
      required: false,
      examples: ['Priority', 'Position', 'Queue', 'Order'],
    },
    {
      key: 'date',
      label: 'Date',
      description: 'Start date, due date, or completion date',
      icon: Calendar,
      required: false,
      examples: ['Date', 'Due Date', 'Start Date', 'Completion'],
    },
  ];

  // Template data for download
  const templateData = [
    [
      'Client ID',
      'Client Name',
      'Email Address',
      'Status',
      'Priority',
      'Due Date',
      'Notes',
    ],
    [
      '001',
      'John Smith',
      'john@example.com',
      'In Progress',
      '1',
      '2025-06-15',
      'Reviewing documents',
    ],
    [
      '002',
      'Sarah Johnson',
      'sarah@example.com',
      'Completed',
      '0',
      '2025-05-20',
      'Project finished',
    ],
    [
      '003',
      'Mike Davis',
      'mike@example.com',
      'In Queue',
      '3',
      '2025-06-30',
      'Waiting to start',
    ],
    [
      '004',
      'Lisa Wilson',
      'lisa@example.com',
      'Review',
      '2',
      '2025-06-10',
      'Final review stage',
    ],
    [
      '005',
      'Robert Garcia',
      'robert@example.com',
      'Awaiting Documents',
      '4',
      '2025-07-01',
      'Needs client input',
    ],
  ];

  const steps = [
    {
      id: 1,
      title: 'Choose Method',
      description: 'Import existing data or start with template',
    },
    {
      id: 2,
      title: 'Connect Sheet',
      description: 'Analyze your data structure',
    },
    { id: 3, title: 'Map Columns', description: 'Verify data mapping' },
    { id: 4, title: 'Configure', description: 'Finalize settings' },
  ];

  // Extract sheet ID from Google Sheets URL
  const extractSheetId = (url) => {
    const match = url.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
    return match ? match[1] : null;
  };

  // Analyze Google Sheets data using your Supabase proxy with auth
  const analyzeSheet = async () => {
    console.log('🔍 Starting sheet analysis...');
    setIsAnalyzing(true);

    try {
      if (!user) {
        console.error('❌ No user found');
        throw new Error('You must be logged in to analyze Google Sheets');
      }

      const sheetId = extractSheetId(sheetUrl);
      if (!sheetId) {
        console.error('❌ Invalid sheet URL:', sheetUrl);
        throw new Error('Invalid Google Sheets URL');
      }

      console.log('✅ User found:', user.id);
      console.log('✅ Sheet ID extracted:', sheetId);

      // Replace your current session refresh code with this
      console.log('🔐 Getting fresh session...');

      let authToken = null;
      let sessionRetrievalTimeout = false;

      // First, try to refresh the session with a timeout
      try {
        console.log('🔐 Attempting session refresh...');

        // Create a promise that times out after 5 seconds
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => {
            sessionRetrievalTimeout = true;
            reject(new Error('Session refresh timeout'));
          }, 5000);
        });

        // Race the session refresh against the timeout
        const refreshResult = await Promise.race([
          supabase.auth.refreshSession(),
          timeoutPromise,
        ]);

        if (refreshResult.data?.session) {
          authToken = refreshResult.data.session.access_token;
          console.log('✅ Session refreshed successfully');
        } else if (refreshResult.error) {
          console.warn(
            '⚠️ Session refresh error:',
            refreshResult.error.message
          );
        }
      } catch (refreshError) {
        console.warn('⚠️ Session refresh exception:', refreshError.message);

        if (sessionRetrievalTimeout) {
          console.log('⏱️ Session refresh timed out, trying alternatives');
        }
      }

      // If refresh didn't work or timed out, try localStorage
      if (!authToken) {
        try {
          console.log('🔐 Attempting to get token from localStorage...');
          const projectRef =
            process.env.REACT_APP_SUPABASE_URL?.split('//')[1]?.split('.')[0];
          const storageKey = `sb-${projectRef}-auth-token`;
          console.log('🔐 Looking for token in localStorage key:', storageKey);

          const supabaseAuth = localStorage.getItem(storageKey);
          if (supabaseAuth) {
            try {
              const authData = JSON.parse(supabaseAuth);
              if (authData && authData.access_token) {
                console.log('✅ Found token in localStorage');

                // Check token expiration if possible
                if (authData.expires_at) {
                  const expiresAt = new Date(authData.expires_at * 1000);
                  const now = new Date();
                  if (expiresAt > now) {
                    console.log(
                      '✅ Token is still valid, expires:',
                      expiresAt.toISOString()
                    );
                    authToken = authData.access_token;
                  } else {
                    console.log('⚠️ Token from localStorage is expired');
                  }
                } else {
                  // If no expiration info, use it anyway
                  authToken = authData.access_token;
                }
              }
            } catch (parseError) {
              console.error('❌ Error parsing localStorage token:', parseError);
            }
          } else {
            console.log('❌ No token found in localStorage');
          }
        } catch (e) {
          console.error('❌ Error accessing localStorage:', e);
        }
      }

      // If still no token, try getUser directly
      if (!authToken && user) {
        console.log('🔐 Attempting to use user data for authentication...');

        try {
          // For development only - create a basic JWT-like structure
          // WARNING: This is only for development and won't work in production
          if (process.env.NODE_ENV === 'development') {
            console.log(
              '🧪 Development environment detected, creating development token'
            );

            // Check if user has a JWT-compatible ID
            if (user.id && typeof user.id === 'string') {
              // Format payload with minimal required fields
              const payload = {
                sub: user.id,
                exp: Math.floor(Date.now() / 1000) + 3600, // 1 hour from now
                iat: Math.floor(Date.now() / 1000),
                role: 'authenticated',
              };

              // Create a basic JWT-like structure (not for production use)
              const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
              const encodedPayload = btoa(JSON.stringify(payload));
              const signature = btoa('development_signature'); // Not a real signature

              authToken = `${header}.${encodedPayload}.${signature}`;
              console.log('🔑 Created development token for testing');
            }
          }
        } catch (devTokenError) {
          console.error('❌ Error creating development token:', devTokenError);
        }
      }

      // Final fallback for development environments only
      if (!authToken && process.env.NODE_ENV === 'development') {
        try {
          console.log(
            '🔐 All token methods failed, attempting development login'
          );

          // This should only be used in development with dummy credentials
          if (
            process.env.REACT_APP_DEV_EMAIL &&
            process.env.REACT_APP_DEV_PASSWORD
          ) {
            const { data, error } = await supabase.auth.signInWithPassword({
              email: process.env.REACT_APP_DEV_EMAIL,
              password: process.env.REACT_APP_DEV_PASSWORD,
            });

            if (error) {
              console.error('❌ Development login failed:', error.message);
            } else if (data?.session?.access_token) {
              authToken = data.session.access_token;
              console.log('✅ Development login successful');
            }
          }
        } catch (devLoginError) {
          console.error('❌ Development login error:', devLoginError);
        }
      }

      // If all methods failed, show a clear error
      if (!authToken) {
        console.error('❌ All authentication methods failed');
        throw new Error(
          'Could not authenticate. Please try logging out and back in.'
        );
      }

      console.log(
        '✅ Auth token obtained:',
        authToken.substring(0, 10) + '...'
      );

      // Add token debugging on client side
      try {
        const parts = authToken.split('.');
        if (parts.length === 3) {
          try {
            const payload = JSON.parse(atob(parts[1]));
            console.log('🔍 Token payload:', {
              sub: payload.sub,
              exp: payload.exp,
              expiresAt: payload.exp
                ? new Date(payload.exp * 1000).toISOString()
                : 'unknown',
              isExpired: payload.exp
                ? Date.now() > payload.exp * 1000
                : 'unknown',
            });
          } catch (decodeError) {
            console.warn('⚠️ Could not decode token payload:', decodeError);
          }
        } else {
          console.warn(
            '⚠️ Token does not appear to be a valid JWT (should have 3 parts)'
          );
        }
      } catch (tokenDebugError) {
        console.warn('⚠️ Error debugging token:', tokenDebugError);
      }

      const headers = {
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      };

      const url = `/.netlify/functions/google-sheets-proxy?sheetId=${sheetId}`;
      console.log('📡 Making request to:', url);

      // Add debug header to help with troubleshooting
      headers['X-Debug-User-Id'] = user.id;

      // Add a timeout to the fetch request
      console.log('📡 Starting fetch request...');
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.error('⏰ Request timeout after 30 seconds');
        controller.abort();
      }, 30000);
      // eslint-disable-next-line
      const response = await fetch(url, {
        method: 'GET',
        headers,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Rest of your code remains the same...

      // Existing code for handling response, analyzing data, etc.
    } catch (error) {
      console.error('💥 Sheet analysis error:', error);
      setAnalysisResult({
        success: false,
        error: error.message,
        suggestions: [],
        warnings: [`Failed to access spreadsheet: ${error.message}`],
      });
    } finally {
      console.log('🔄 Setting isAnalyzing to false');
      setIsAnalyzing(false);
    }
  };
  const handleColumnMapping = (targetKey, sourceIndex) => {
    setColumnMapping((prev) => ({
      ...prev,
      [targetKey]: sourceIndex,
    }));
  };

  const downloadTemplate = () => {
    // Convert template data to CSV
    const csvContent = templateData
      .map((row) => row.map((cell) => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'trackersheets-template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const copyTemplateUrl = () => {
    const templateUrl =
      'https://docs.google.com/spreadsheets/d/1ABC123/edit#gid=0';
    navigator.clipboard.writeText(templateUrl);
    setCopiedTemplate(true);
    setTimeout(() => setCopiedTemplate(false), 2000);
  };

  const validateSheetUrl = (url) => {
    const googleSheetsPattern = /docs\.google\.com\/spreadsheets/;
    return googleSheetsPattern.test(url);
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 80) return 'text-green-600';
    if (confidence >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getMappingStatus = (targetKey) => {
    if (columnMapping[targetKey] !== undefined) {
      return 'mapped';
    }
    const target = targetColumns.find((col) => col.key === targetKey);
    return target?.required ? 'required' : 'optional';
  };

  const canProceed = () => {
    const requiredColumns = targetColumns.filter((col) => col.required);
    return requiredColumns.every((col) => columnMapping[col.key] !== undefined);
  };

  const finalizeSetup = async () => {
    setIsAnalyzing(true);

    try {
      // Extract sheet ID for saving
      const sheetId = extractSheetId(sheetUrl);

      // Save configuration to data context
      const config = {
        sheetId,
        sheetUrl,
        columnMapping,
        setupMethod,
        detectedHeaders: detectedData.headers,
        confidence: analysisResult.confidence,
        createdAt: new Date().toISOString(),
      };

      saveSheetConfig(config);

      // Simulate API call delay
      await new Promise((resolve) => setTimeout(resolve, 1500));

      setIsConnected(true);
      setIsAnalyzing(false);
      setCurrentStep(4);
    } catch (error) {
      console.error('Error finalizing setup:', error);
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div
                className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                  currentStep >= step.id
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'border-gray-300 text-gray-500'
                }`}
              >
                {currentStep > step.id ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  <span className="font-medium">{step.id}</span>
                )}
              </div>
              <div className="ml-3">
                <div
                  className={`text-sm font-medium ${
                    currentStep >= step.id ? 'text-gray-900' : 'text-gray-500'
                  }`}
                >
                  {step.title}
                </div>
                <div className="text-xs text-gray-500">{step.description}</div>
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`mx-6 h-0.5 w-16 ${
                    currentStep > step.id ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Step 1: Choose Method */}
      {currentStep === 1 && (
        <div className="space-y-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Google Sheets Integration
            </h2>
            <p className="text-gray-600">
              Choose how you'd like to set up your client data
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Import Existing Data */}
            <div
              className={`p-6 border-2 rounded-lg cursor-pointer transition-all ${
                setupMethod === 'import'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-blue-300'
              }`}
              onClick={() => setSetupMethod('import')}
            >
              <div className="flex items-center mb-4">
                <Upload className="w-8 h-8 text-blue-600 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Import Existing Data
                </h3>
              </div>
              <p className="text-gray-600 mb-4">
                Connect your existing Google Sheet and we'll automatically
                detect and map your client data.
              </p>
              <div className="space-y-2">
                <div className="flex items-center text-sm text-green-600">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Smart column detection
                </div>
                <div className="flex items-center text-sm text-green-600">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Keep existing workflow
                </div>
                <div className="flex items-center text-sm text-green-600">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  No data migration needed
                </div>
              </div>
            </div>

            {/* Start with Template */}
            <div
              className={`p-6 border-2 rounded-lg cursor-pointer transition-all ${
                setupMethod === 'template'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-blue-300'
              }`}
              onClick={() => setSetupMethod('template')}
            >
              <div className="flex items-center mb-4">
                <FileSpreadsheet className="w-8 h-8 text-green-600 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Start with Template
                </h3>
              </div>
              <p className="text-gray-600 mb-4">
                Download our optimized template or copy our Google Sheet
                template to get started quickly.
              </p>
              <div className="space-y-2">
                <div className="flex items-center text-sm text-green-600">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Pre-configured columns
                </div>
                <div className="flex items-center text-sm text-green-600">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Best practices included
                </div>
                <div className="flex items-center text-sm text-green-600">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Sample data provided
                </div>
              </div>
            </div>
          </div>

          {setupMethod && (
            <div className="flex justify-end">
              <button
                onClick={() => setCurrentStep(2)}
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                Continue
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Step 2: Connect Sheet */}
      {currentStep === 2 && (
        <div className="space-y-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {setupMethod === 'import'
                ? 'Connect Your Google Sheet'
                : 'Get Your Template'}
            </h2>
            <p className="text-gray-600">
              {setupMethod === 'import'
                ? "Enter your Google Sheets URL and we'll analyze your data structure"
                : 'Download or copy our template to get started'}
            </p>
          </div>

          {setupMethod === 'import' ? (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <HelpCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-blue-900">
                      Before you start
                    </h4>
                    <p className="text-blue-800 text-sm mt-1">
                      Make sure your Google Sheet is shared with "Anyone with
                      the link can view" or add trackersheets@gmail.com as a
                      viewer.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Google Sheets URL
                </label>
                <div className="flex">
                  <input
                    type="url"
                    value={sheetUrl}
                    onChange={(e) => setSheetUrl(e.target.value)}
                    placeholder="https://docs.google.com/spreadsheets/d/..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button
                    onClick={analyzeSheet}
                    disabled={!validateSheetUrl(sheetUrl) || isAnalyzing}
                    className="px-4 py-2 bg-blue-600 text-white rounded-r-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isAnalyzing ? (
                      <RefreshCw className="w-5 h-5 animate-spin" />
                    ) : (
                      <Zap className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {sheetUrl && !validateSheetUrl(sheetUrl) && (
                  <p className="text-red-600 text-sm mt-1">
                    Please enter a valid Google Sheets URL
                  </p>
                )}
              </div>

              {isAnalyzing && (
                <div className="text-center py-8">
                  <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
                  <p className="text-gray-600">Analyzing your spreadsheet...</p>
                  <p className="text-sm text-gray-500 mt-1">
                    This may take a few seconds
                  </p>
                </div>
              )}

              {analysisResult && (
                <div
                  className={`border rounded-lg p-6 ${
                    analysisResult.success
                      ? 'bg-white border-gray-200'
                      : 'bg-red-50 border-red-200'
                  }`}
                >
                  <div className="flex items-center mb-4">
                    {analysisResult.success ? (
                      <CheckCircle className="w-6 h-6 text-green-600 mr-3" />
                    ) : (
                      <AlertCircle className="w-6 h-6 text-red-600 mr-3" />
                    )}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {analysisResult.success
                          ? 'Analysis Complete'
                          : 'Analysis Failed'}
                      </h3>
                      {analysisResult.success ? (
                        <p className="text-sm text-gray-600">
                          Found {analysisResult.rowCount} rows with{' '}
                          <span
                            className={`font-medium ${getConfidenceColor(
                              analysisResult.confidence
                            )}`}
                          >
                            {analysisResult.confidence}% confidence
                          </span>
                        </p>
                      ) : (
                        <p className="text-sm text-red-600">
                          {analysisResult.error}
                        </p>
                      )}
                    </div>
                  </div>

                  {analysisResult.success &&
                    analysisResult.suggestions.length > 0 && (
                      <div className="mb-4">
                        <h4 className="font-medium text-gray-900 mb-2">
                          ✅ Detected Features:
                        </h4>
                        <ul className="space-y-1">
                          {analysisResult.suggestions.map(
                            (suggestion, index) => (
                              <li
                                key={index}
                                className="text-sm text-gray-600 flex items-center"
                              >
                                <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                                {suggestion}
                              </li>
                            )
                          )}
                        </ul>
                      </div>
                    )}

                  {analysisResult.warnings.length > 0 && (
                    <div className="mb-4">
                      <h4 className="font-medium text-gray-900 mb-2">
                        {analysisResult.success
                          ? '⚠️ Recommendations:'
                          : '❌ Issues:'}
                      </h4>
                      <ul className="space-y-1">
                        {analysisResult.warnings.map((warning, index) => (
                          <li
                            key={index}
                            className={`text-sm flex items-center ${
                              analysisResult.success
                                ? 'text-amber-700'
                                : 'text-red-700'
                            }`}
                          >
                            <AlertCircle
                              className={`w-4 h-4 mr-2 ${
                                analysisResult.success
                                  ? 'text-amber-500'
                                  : 'text-red-500'
                              }`}
                            />
                            {warning}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {!analysisResult.success && (
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
                      <h5 className="font-medium text-blue-900 mb-2">
                        Troubleshooting Tips:
                      </h5>
                      <ul className="text-sm text-blue-800 space-y-1">
                        <li>
                          • Make sure your sheet is shared with "Anyone with the
                          link can view"
                        </li>
                        <li>• Check that the URL is correct and accessible</li>
                        <li>
                          • Ensure your sheet has headers in the first row
                        </li>
                        <li>• Try refreshing the page and trying again</li>
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Download CSV Template */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <Download className="w-6 h-6 text-blue-600 mr-3" />
                    <h3 className="text-lg font-semibold text-gray-900">
                      Download CSV Template
                    </h3>
                  </div>
                  <p className="text-gray-600 mb-4">
                    Get a pre-formatted CSV file that you can open in Excel or
                    Google Sheets.
                  </p>
                  <button
                    onClick={downloadTemplate}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Template
                  </button>
                </div>

                {/* Copy Google Sheets Template */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <Copy className="w-6 h-6 text-green-600 mr-3" />
                    <h3 className="text-lg font-semibold text-gray-900">
                      Copy Google Sheets Template
                    </h3>
                  </div>
                  <p className="text-gray-600 mb-4">
                    Use our ready-made Google Sheets template with formulas and
                    formatting.
                  </p>
                  <div className="flex space-x-2">
                    <button
                      onClick={copyTemplateUrl}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      {copiedTemplate ? (
                        <Check className="w-4 h-4 mr-2" />
                      ) : (
                        <Copy className="w-4 h-4 mr-2" />
                      )}
                      {copiedTemplate ? 'Copied!' : 'Copy Link'}
                    </button>
                    <a
                      href="https://docs.google.com/spreadsheets/d/1ABC123/edit#gid=0"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Open Template
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">
                  Template Preview:
                </h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className="bg-gray-100">
                        {templateData[0].map((header, index) => (
                          <th
                            key={index}
                            className="px-2 py-1 text-left font-medium text-gray-700 border border-gray-300"
                          >
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {templateData.slice(1, 3).map((row, rowIndex) => (
                        <tr key={rowIndex}>
                          {row.map((cell, cellIndex) => (
                            <td
                              key={cellIndex}
                              className="px-2 py-1 text-gray-600 border border-gray-300"
                            >
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setCurrentStep(1)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <ArrowLeft className="mr-2 h-5 w-5" />
                  Back
                </button>
                <button
                  onClick={() => {
                    // For template method, skip to final step
                    setCurrentStep(4);
                    setIsConnected(true);
                  }}
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  Setup Complete
                  <ArrowRight className="ml-2 h-5 w-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step 3: Map Columns */}
      {currentStep === 3 && detectedData && (
        <div className="space-y-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Map Your Columns
            </h2>
            <p className="text-gray-600">
              Verify our automatic mapping or adjust which columns contain your
              client data
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Column Mapping */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Column Mapping
              </h3>

              {targetColumns.map((target) => {
                const Icon = target.icon;
                const status = getMappingStatus(target.key);

                return (
                  <div
                    key={target.key}
                    className="border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex items-start">
                      <Icon
                        className={`w-5 h-5 mr-3 mt-0.5 ${
                          status === 'mapped'
                            ? 'text-green-600'
                            : status === 'required'
                            ? 'text-red-600'
                            : 'text-gray-400'
                        }`}
                      />
                      <div className="flex-1">
                        <div className="flex items-center">
                          <h4 className="font-medium text-gray-900">
                            {target.label}
                          </h4>
                          {target.required && (
                            <span className="ml-2 text-xs text-red-600">
                              Required
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-2">
                          {target.description}
                        </p>

                        <select
                          value={columnMapping[target.key] || ''}
                          onChange={(e) =>
                            handleColumnMapping(
                              target.key,
                              e.target.value
                                ? parseInt(e.target.value)
                                : undefined
                            )
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Select column...</option>
                          {detectedData.headers.map((header, index) => (
                            <option key={index} value={index}>
                              {header}
                            </option>
                          ))}
                        </select>

                        <p className="text-xs text-gray-500 mt-1">
                          Examples: {target.examples.join(', ')}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Data Preview */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Data Preview
              </h3>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr>
                        {detectedData.headers.map((header, index) => (
                          <th
                            key={index}
                            className={`px-2 py-1 text-left font-medium border-b border-gray-300 ${
                              Object.values(columnMapping).includes(index)
                                ? 'bg-blue-100 text-blue-800'
                                : 'text-gray-700'
                            }`}
                          >
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {detectedData.rows.slice(0, 3).map((row, rowIndex) => (
                        <tr key={rowIndex}>
                          {row.map((cell, cellIndex) => (
                            <td
                              key={cellIndex}
                              className={`px-2 py-1 border-b border-gray-200 ${
                                Object.values(columnMapping).includes(cellIndex)
                                  ? 'bg-blue-50'
                                  : ''
                              }`}
                            >
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Showing first 3 rows. Blue highlighting shows mapped columns.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-between">
            <button
              onClick={() => setCurrentStep(2)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <ArrowLeft className="mr-2 h-5 w-5" />
              Back
            </button>
            <button
              onClick={finalizeSetup}
              disabled={!canProceed() || isAnalyzing}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {isAnalyzing ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                  Connecting...
                </>
              ) : (
                <>
                  Finalize Setup
                  <ArrowRight className="ml-2 h-5 w-5" />
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Success */}
      {currentStep === 4 && (
        <div className="text-center space-y-6">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {setupMethod === 'import'
                ? 'Google Sheets Connected!'
                : 'Template Ready!'}
            </h2>
            <p className="text-gray-600">
              {setupMethod === 'import'
                ? 'Your spreadsheet is now connected and ready to power your client portal.'
                : 'Your template is ready. Upload it to Google Sheets and return here to connect it.'}
            </p>
          </div>

          {isConnected && setupMethod === 'import' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-left max-w-2xl mx-auto">
              <h3 className="font-semibold text-green-900 mb-3">
                ✅ Setup Complete
              </h3>
              <div className="space-y-2 text-sm text-green-800">
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Connected to Google Sheets
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Column mapping configured
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {detectedData?.rows?.length || 0} client records detected
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Portal ready for configuration
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setCurrentPage('configurator')}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              <Eye className="mr-2 h-5 w-5" />
              Configure Portal
            </button>

            <button
              onClick={() => setCurrentPage('portals')}
              className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <Edit3 className="mr-2 h-5 w-5" />
              Manage Clients
            </button>
          </div>

          {setupMethod === 'template' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-2xl mx-auto">
              <h4 className="font-medium text-blue-900 mb-2">Next Steps:</h4>
              <ol className="text-sm text-blue-800 space-y-1 text-left">
                <li>1. Upload your template to Google Sheets</li>
                <li>2. Fill in your client data</li>
                <li>
                  3. Return here and use "Import Existing Data" to connect it
                </li>
                <li>4. Configure your client portal settings</li>
              </ol>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GoogleSheetsSetupWizard;
